import React, { useState } from 'react';
import { LicenseService } from '../../firebase/services/licenseService';
import { useAuth } from '../../contexts/AuthContext';

interface LicenseVerifierProps {
  licenseKey: string;
  licenseType?: string;
}

const AVAILABLE_CATEGORIES = [
  { value: 'social', label: 'Social (YouTube, Twitch, Instagram, TikTok)' },
  { value: 'layout', label: 'Layout (Carousel, Blog Grid, Heading, Masonry)' },
  { value: 'ecommerce', label: 'E-commerce (Product Grids, Shopping Carts, Wishlist)' },
  { value: 'complete', label: 'Complete (All Categories)' }
];

export default function LicenseVerifier({ licenseKey, licenseType }: LicenseVerifierProps) {
  const { currentUser } = useAuth();
  const [domain, setDomain] = useState('');
  const [pluginVersion, setPluginVersion] = useState('');
  const [pluginIdentifier, setPluginIdentifier] = useState('lava-addons');
  const [installationId, setInstallationId] = useState('');
  const [autoAddDomain, setAutoAddDomain] = useState(false);
  const [requestedCategory, setRequestedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    isValid: boolean;
    message?: string;
    licenseType?: string;
    expiryDate?: string;
    userEmail?: string;
    maxSites?: number;
    currentSites?: number;
    accessibleCategories?: string[];
    requestedCategory?: string;
    totalLicenses?: number;
  } | null>(null);

  const handleVerify = async () => {
    if (!domain.trim()) {
      alert('Please enter a domain');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const additionalData = {
        userEmail: currentUser?.email || undefined,
        pluginVersion: pluginVersion || '1.0.0',
        pluginIdentifier: pluginIdentifier || 'lava-addons',
        installationId: installationId || `install-${Date.now()}`,
        licenseType: licenseType,
        autoAddDomain: autoAddDomain,
        requestedCategory: requestedCategory || undefined
      };

      const verificationResult = await LicenseService.verifyLicense(licenseKey, domain.trim(), additionalData);
      setResult(verificationResult);
    } catch (error) {
      console.error('Error verifying license:', error);
      setResult({
        isValid: false,
        message: 'Failed to verify license. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Verify License</h4>
      
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Domain *
          </label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="Enter domain (e.g., example.com)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Plugin Version
            </label>
            <input
              type="text"
              value={pluginVersion}
              onChange={(e) => setPluginVersion(e.target.value)}
              placeholder="1.0.0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Plugin Identifier
            </label>
            <input
              type="text"
              value={pluginIdentifier}
              onChange={(e) => setPluginIdentifier(e.target.value)}
              placeholder="lava-addons"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Installation ID (Optional)
          </label>
          <input
            type="text"
            value={installationId}
            onChange={(e) => setInstallationId(e.target.value)}
            placeholder="Unique installation identifier"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Test Category Access (Optional)
          </label>
          <select
            value={requestedCategory}
            onChange={(e) => setRequestedCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            disabled={loading}
          >
            <option value="">Select a category to test...</option>
            {AVAILABLE_CATEGORIES.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="autoAddDomain"
            checked={autoAddDomain}
            onChange={(e) => setAutoAddDomain(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="autoAddDomain" className="ml-2 text-sm text-gray-700">
            Automatically add domain to license if not already licensed
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={handleVerify}
            disabled={loading || !domain.trim()}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify License'}
          </button>
        </div>
      </div>

      {result && (
        <div className={`mt-4 p-3 rounded-md ${
          result.isValid 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {result.isValid ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className={`text-sm font-medium ${
                result.isValid ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.isValid ? 'License Valid' : 'License Invalid'}
              </p>
              {result.message && (
                <p className={`text-sm ${
                  result.isValid ? 'text-green-700' : 'text-red-700'
                }`}>
                  {result.message}
                </p>
              )}
              <div className="mt-2 space-y-1">
                {result.licenseType && (
                  <p className="text-xs text-gray-600">
                    License Type: {result.licenseType}
                  </p>
                )}
                {result.expiryDate && (
                  <p className="text-xs text-gray-600">
                    Expires: {new Date(result.expiryDate).toLocaleDateString()}
                  </p>
                )}
                {result.userEmail && (
                  <p className="text-xs text-gray-600">
                    User: {result.userEmail}
                  </p>
                )}
                {result.maxSites && (
                  <p className="text-xs text-gray-600">
                    Total Sites: {result.currentSites || 0}/{result.maxSites}
                  </p>
                )}
                {result.totalLicenses && result.totalLicenses > 1 && (
                  <p className="text-xs text-blue-600 font-medium">
                    Multiple Licenses: {result.totalLicenses} active licenses
                  </p>
                )}
                {result.accessibleCategories && result.accessibleCategories.length > 0 && (
                  <p className="text-xs text-gray-600">
                    Accessible Categories: {result.accessibleCategories.join(', ')}
                  </p>
                )}
                {result.requestedCategory && (
                  <p className="text-xs text-gray-600">
                    Tested Category: {result.requestedCategory}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 